import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import { format } from "date-fns";

import FormikInput from "@components/form/FormikInput";
import FormikDatepicker from "@components/form/FormikDatepicker";
import FormikRadioGroup from "@components/form/FormikRadioGroup";
import { saveRate } from "@service/PayRateService";
import savePayRateSchema from "@validators/savePayRateSchema";
import  Button  from "@components/Button";
import { IUpdatePayRateDTO } from "../../interfaces/IUpdatePayRateDTO";
import { SnackbarContext } from '@components/constant_components/providers/SnackbarProvider';
import Spinner from "@components/Spinner";

interface IPayRateFieldsProps {
  worker_id: number;
}

interface iFormPayRateValues {
  payrate_start_date: Date;
  payrate_type: 'hourly' | 'weekly' | 'daily'
  payrate_rate: string;
}

export function PayRateFields({ worker_id}: IPayRateFieldsProps): JSX.Element {
  const { t } = useTranslation();
  const { triggerSnackbar } = useContext(SnackbarContext);

  const initialPayRateValues: iFormPayRateValues = {
    payrate_start_date: new Date(),
    payrate_rate: '',
    payrate_type: 'hourly',
  };

  const payrateValidationSchema = savePayRateSchema(t);

  const handlePayRateSubmit = useCallback(
    async (values: iFormPayRateValues,
       { setSubmitting }: import("formik").FormikHelpers<iFormPayRateValues>
    ) => {
      try {
        const data = {
          worker_id: worker_id,
          value: values.payrate_rate,
          rate_type: values.payrate_type,
          start_date: format(values.payrate_start_date, "yyyy-MM-dd"),
        } as IUpdatePayRateDTO;
        await saveRate(data);
        triggerSnackbar('Pay rate updated successfully!');
      } catch (err: any) {
        triggerSnackbar(`Error updating pay rate: ${err?.message}`);
      } finally {
         setSubmitting(false);
      }
    },
    [worker_id, triggerSnackbar]
  );

  return (
    <Formik<iFormPayRateValues>
      initialValues={initialPayRateValues}
      validationSchema={payrateValidationSchema}
      onSubmit={handlePayRateSubmit}
      >
      {({ errors, touched, isSubmitting }) => (
        <div className="tw-px-6 tw-pb-6" data-testid="form">
          <Form>
              <div className="tw-border tw-p-4 tw-border-gray-500">
                <span className="tw-text-sm tw-bg-gray-400 tw-p-1 tw-rounded">PAY RATE</span>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mt-2">
                  <Field
                    name="payrate_start_date"
                    label={t("Rate.start_date")}
                    as={FormikDatepicker}
                    error={
                      touched.payrate_start_date && errors.payrate_start_date
                    }
                  />
                  <Field
                    name="payrate_type"
                    label={t("Rate.type")}
                    options={[
                      { value: "hourly", label: t("Rate.hourly") },
                      { value: "daily", label: t("Rate.daily") },
                      { value: "weekly", label: t("Rate.weekly") },
                    ]}
                    as={FormikRadioGroup}
                    error={touched.payrate_type && errors.payrate_type}
                  />
                  <Field
                    name="payrate_rate"
                    label={t("Rate.rate")}
                    as={FormikInput}
                    inputMode="numeric"
                    error={touched.payrate_rate && errors.payrate_rate}
                  />
                </div>
                <div className="tw-flex tw-grid-cols-1 md:tw-grid-cols-1 tw-justify-end tw-mt-2">
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="md:tw-w-[31%]"
                  >
                    {isSubmitting ? <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" /> : 'Pay Rate Update'}
                  </Button>
                </div>
              </div>
          </Form>
        </div>
      )}
    </Formik>
  );
}